import React, { forwardRef, useEffect, useMemo, useState } from 'react';
import type { AvatarProps } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { getBgColor } from '@/mui/helpers';

import Avatar from '../../Avatar/Avatar';

type Props = {
  firstName?: string;
  lastName?: string;
  size?: 'xSmall' | 'small' | 'medium' | 'large';
};

const UserAvatar = forwardRef<HTMLDivElement, AvatarProps & Props>((props, ref) => {
  const { id, firstName = '', lastName = '', ...avatarProps } = props;

  const theme = useTheme();
  const [useAvatarImg, setImgState] = useState(false);
  const bg = useMemo(() => getBgColor(`${id}${firstName}${lastName}`), [id, firstName, lastName]);

  const firstLetter = firstName[0];
  const lastLetter = lastName[0];

  useEffect(() => {
    setImgState(false);

    if (props.src) {
      const img = document.createElement('img');
      img.src = props.src;

      img.addEventListener('load', () => {
        setImgState(true);

        img.remove();
      });

      img.addEventListener('error', () => {
        setImgState(false);

        img.remove();
      });
    }
  }, [props.src]);

  return (
    <Avatar
      {...avatarProps}
      size={props.size ?? 'medium'}
      ref={ref}
      style={{
        ...avatarProps.style,
        color: theme.palette.getContrastText(bg),
        backgroundColor: bg,
      }}
      aria-label={[firstName, lastName].join('')}
    >
      {!useAvatarImg && (
        <>
          {firstLetter}
          {!!firstLetter && !!lastLetter && (
            <span
              style={{
                // eslint-disable-next-line local-rules/no-inline-styles
                opacity: 0,
                // eslint-disable-next-line local-rules/no-inline-styles
                fontSize: '0.3em',
              }}
            >
              .
            </span>
          )}
          {lastLetter}
        </>
      )}
    </Avatar>
  );
});

UserAvatar.displayName = 'UserAvatar';

export default UserAvatar;
